/**
 * NewsFeedTier - horizontal-scroll strip of NewsCard, with chevron
 * arrows, "See All" link, tier header, and CTA-driven empty state.
 *
 * One instance per tier (MY PLAYERS / MY LEAGUES / NFL NEWS). Feed
 * data supplied by parent - no fetching in this component; keeps it
 * pure and easy to reuse.
 */
import { useRef } from "react";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import NewsCard from "./NewsCard";

function relativeTime(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
}

export default function NewsFeedTier({
  title,
  items,
  lastUpdated,
  emptyCTA,
  onRefresh,
  onInternalClick,
  onPlayerClick,
  onSeeAll,
}) {
  const scrollRef = useRef(null);

  const scroll = (delta) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: delta, behavior: "smooth" });
  };

  return (
    <section className="mb-6" data-testid={`news-tier-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-baseline justify-between mb-2">
        <div className="flex items-baseline gap-2">
          <h3 className="font-display font-black uppercase text-lg tracking-tight text-[#F5F5F5]">
            {title}
          </h3>
          {items?.length > 0 && (
            <span className="text-[11px] text-[#A0A0A0]">
              ({items.length} {items.length === 1 ? "item" : "items"})
            </span>
          )}
          {lastUpdated && (
            <span className="text-[10px] text-[#404040]">
              · updated {relativeTime(lastUpdated)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              className="text-[#A0A0A0] hover:text-[#FF6B00] transition-colors p-1"
              title="Refresh"
              data-testid={`news-refresh-${title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <RefreshCw size={12} />
            </button>
          )}
          {onSeeAll && items?.length > 0 && (
            <button
              type="button"
              onClick={onSeeAll}
              className="text-[10px] uppercase tracking-widest text-[#FF6B00] font-display font-bold hover:underline"
              data-testid={`news-see-all-${title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              See All
            </button>
          )}
        </div>
      </div>

      {items && items.length > 0 ? (
        <div className="relative group">
          <button
            type="button"
            onClick={() => scroll(-320)}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity bg-[#111111]/80 backdrop-blur-sm rounded-full p-1.5 border border-[#2A2A2A] hover:border-[#FF6B00] disabled:opacity-30"
            aria-label="Scroll left"
          >
            <ChevronLeft size={16} className="text-[#F5F5F5]" />
          </button>
          <div
            ref={scrollRef}
            className="flex gap-3 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-1 px-1"
            style={{ scrollbarWidth: "none" }}
          >
            {items.map((it) => (
              <div key={it.id} className="snap-start">
                <NewsCard
                  item={it}
                  onInternalClick={onInternalClick}
                  onPlayerClick={onPlayerClick}
                />
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => scroll(320)}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity bg-[#111111]/80 backdrop-blur-sm rounded-full p-1.5 border border-[#2A2A2A] hover:border-[#FF6B00]"
            aria-label="Scroll right"
          >
            <ChevronRight size={16} className="text-[#F5F5F5]" />
          </button>
        </div>
      ) : (
        <div
          className="rounded-xl border border-dashed border-[#2A2A2A] bg-[#141414] p-5 text-center"
          data-testid={`news-empty-${title.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <p className="text-sm text-[#A0A0A0]">{emptyCTA}</p>
        </div>
      )}
    </section>
  );
}
